import { Button, Modal, Form, Input, DatePicker, Card, Select, message, Upload } from "antd";
import type { UploadProps } from "antd";
import dayjs from "dayjs";
import { useState, useEffect, useRef } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { localUploadBaseProps } from "../../../utils/localUploadRequest";

import "./storyTimeModal.scss";
import StoryTimeDeleteModal from "./components/storyTimeDeleteModal";
import type { StoryTimeListType } from "./content/storyTimeType"
import { header } from "../utils/zippyAdminHeader";
import { Colors } from "../../../constants/Colors"
import { data as dataSetting } from "../../../data/data";
import { apiCall } from "../../../utils/apiCall";
import requiredRule from "../../../utils/formRules/requiredRule";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";

const toBase64 = (file: any): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default function StoryTimeModal({
  storyTimeData,
  open,
  type,
  userName,
  setOpenMainModal,
  setEditMainData,
  refresh,
}: {
  storyTimeData: StoryTimeListType | undefined,
  open: boolean,
  type: "Create" | "Edit",
  userName: string,
  setOpenMainModal: any,
  setEditMainData: any,
  refresh: any,
}) {
  const [form] = Form.useForm();
  const { capFilters } = dataSetting;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [removeGroupName, setRemoveGroupName] = useState<number>(0);
  const removeRef = useRef<(name: number) => void>(() => { });

  const props: UploadProps = {
    ...localUploadBaseProps,
    maxCount: 1,
    beforeUpload: async (file) => {
      const isImage = file.type.startsWith("image");
      if (!isImage) {
        message.error(renderAntDMessageConfig("Only upload image file!"));
      }
      return isImage || Upload.LIST_IGNORE;
    },
    onChange: (info) => {
      if (info.file.status === "done") {
        message.success(
          renderAntDMessageConfig(
            `${info.file.name} file uploaded successfully`
          )
        );
      } else if (info.file.status === "error") {
        message.error(
          renderAntDMessageConfig(`${info.file.name} file upload failed.`)
        );
      }
    },
  };

  const closeModal = () => {
    setOpenMainModal(false);
    setEditMainData(undefined);
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  }

  const handleDeleteClick = (name: number) => {
    const groups = form.getFieldValue('groups');
    const deleteId = groups?.[name].storyGroupId;

    if (deleteId === "") removeRef.current(name);
    else {
      setShowDeleteModal(true);
      setRemoveGroupName(name);
    }
  };

  const handleDeleteGroup = () => {
    removeRef.current(removeGroupName)
    closeDeleteModal();
  };

  const handleSave = async () => {
    try {
      await form.validateFields();

      const { storyTitle, thumbnailFile, publishDate, groups } = form.getFieldsValue();

      let base64 = undefined;
      if (thumbnailFile?.[0]?.originFileObj)
        base64 = await toBase64(thumbnailFile[0].originFileObj);

      const payload = {
        title: storyTitle,
        groups: groups,
        operator: userName,
        ...(publishDate ? { publishDate: dayjs(publishDate).format("YYYY-MM-DD") } : {}),
        ...(thumbnailFile?.[0] ? {
          thumbnailFile: {
            fileName: thumbnailFile[0].name,
            ...(base64 ? { base64File: base64.split(",")[1] } : {}),
          }
        } : {}),
      };

      if (type === "Create") {
        payload.groups = payload.groups.map((group: any) => ({ caps: group.caps }));

        const data = await apiCall({
          method: "POST",
          header: header,
          endPoint:
            process.env.REACT_APP_ZIPPY_ADMIN +
            "/stories/admin",
          payload: payload
        });

        if (data && data.status) {
          message.success(data.status);
          closeModal();
          refresh();
        } else {
          message.error(data.response.data.status);
        }
      }
      else {
        payload.groups = payload.groups.map((group: any) => {
          const item: any = { caps: group.caps };
          if (group.storyGroupId !== '') item.groupId = group.storyGroupId;

          return item;
        });

        const data = await apiCall({
          method: "PATCH",
          header: header,
          endPoint:
            process.env.REACT_APP_ZIPPY_ADMIN +
            `/stories/admin/${storyTimeData?.storyId}`,
          payload: payload
        });

        if (data && data.status) {
          message.success(data.status);
          closeModal();
          refresh();
        } else {
          message.error(data.response.data.status);
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (open) form.resetFields();
    if (storyTimeData) {
      form.setFieldsValue({
        storyTitle: storyTimeData.title,
        publishDate: storyTimeData.publishDate ? dayjs(storyTimeData.publishDate) : "",
        groups: storyTimeData.groups,
        ...(storyTimeData.thumbnailName ? {
          thumbnailFile: [
            {
              uid: "0",
              name: storyTimeData.thumbnailName,
              status: "done",
            },
          ]
        } : {}),
      });
    }
  }, [open, storyTimeData, form]);

  return (
    <>
      <Modal
        centered
        title={type + " a new story & content group"}
        open={open}
        onCancel={closeModal}
        maskClosable={false}
        footer={[
          <Button key="save" type="primary" onClick={handleSave}>
            Save
          </Button>,
        ]}
        width={576}
        className="storyTimeModal"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            groups: [{
              caps: capFilters.map(option => option.value)
            }]
          }}
        >
          <Form.Item
            className="form-item"
            label="Story title"
            rules={[requiredRule]}
            name="storyTitle"
          >
            <Input />
          </Form.Item>
          <Form.Item
            className="form-item"
            label="Upload story cover image"
            name="thumbnailFile"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
          >
            <Upload
              {...props}
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            className="form-item"
            label="Publish date"
            name="publishDate"
          >
            <DatePicker
              format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item label="Content group" required>
            <Form.List
              name="groups"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(new Error("This field is required!"));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => {
                removeRef.current = remove;

                return (
                  <>{fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className="content-group-div" >
                      <Card
                        size="small"
                        className="content-group-card"
                        styles={{ header: { background: Colors.tagBackground } }}
                        title={
                          <div className="content-group-card-heard">
                            <span>#{index + 1}</span>
                            {false && fields.length > 1 && (
                              <Button type="link" onClick={() => handleDeleteClick(name)}>
                                Delete
                              </Button>
                            )}
                          </div>
                        }
                      >
                        <Form.Item
                          {...restField}
                          name={[name, 'storyGroupId']}
                          initialValue=""
                          noStyle
                        >
                          <Input hidden />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          label="CAP :"
                          name={[name, 'caps']}
                          rules={[{ required: true, message: "This field is required!" }]}
                          labelCol={{ flex: '60px' }}
                          wrapperCol={{ flex: '390px' }}
                          extra={
                            fields.length > 1 && !form.getFieldValue(['groups', index]) ? (
                              <span style={{ color: "red" }}>
                                Each CAP can only be in one group. Remove it from the other group before adding it here.
                              </span>
                            ) : null
                          }
                        >
                          <Select
                            mode="multiple"
                            suffixIcon={null}
                            options={capFilters}
                            disabled
                          />
                        </Form.Item>
                      </Card>
                    </div>
                  ))}
                    {errors.length > 0 && (
                      <Form.Item>
                        <Form.ErrorList errors={errors} />
                      </Form.Item>
                    )}
                    {false && fields.length < 5 && (
                      <Form.Item>
                        <Button
                          type="primary"
                          onClick={() => add()}
                          block
                          style={{ width: 160 }}
                        >
                          Add content group
                        </Button>
                      </Form.Item>
                    )}
                  </>
                )
              }}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>
      <StoryTimeDeleteModal
        open={showDeleteModal}
        close={closeDeleteModal}
        handleDelete={() => handleDeleteGroup()}
      />
    </>
  );
}
